import { Dispatch, SetStateAction, useEffect } from 'react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Button, DropdownField, FormFieldActionTypes, SwitchField, TextField, useForm } from '@frontend/design-system';
import { OperatoryAvatarSelector } from '../operatory-avatar-selector';
import { EditingRowState, UNASSIGNED_LABEL } from './manage-operatory-table';

export type ColIdType = 'Icon' | 'DisplayName' | 'LocationId' | 'Providers' | 'Hidden';

type EditCellProps = {
  colId: ColIdType;
  editingRowData: EditingRowState | null;
  setEditingRowData: Dispatch<SetStateAction<EditingRowState | null>>;
  locationOptions?: { label: string; value: string }[];
  parentLocationId?: string;
};

export const EditCell = ({
  colId,
  editingRowData,
  setEditingRowData,
  locationOptions,
  parentLocationId,
}: EditCellProps) => {
  const isParentLocationId = editingRowData?.locationId === parentLocationId;
  const isUnassignedLocation = isParentLocationId;

  const { getFieldProps, seedValues } = useForm({
    fields: {
      displayName: {
        type: 'text',
        value: editingRowData?.displayName || '',
      },
      locationId: {
        type: 'dropdown',
        value: editingRowData?.locationId || '',
      },
      hidden: {
        type: 'switch',
        value: !(editingRowData?.hidden ?? false),
      },
    },
    fieldStateReducer: (state, action) => {
      if (action.type === FormFieldActionTypes.Update && action.payload.value !== undefined && editingRowData) {
        const value = action.payload.name === 'hidden' ? !action.payload.value : action.payload.value;
        const updatedData = { ...editingRowData, [action.payload.name]: value };

        if (action.payload.name === 'locationId') {
          const selectedLocation = locationOptions?.find((option) => option.value === value);
          const isUnassignedLocationSelected = selectedLocation?.label === UNASSIGNED_LABEL;

          if (isUnassignedLocationSelected) {
            updatedData.hidden = true;
          }
        }
        setEditingRowData(updatedData);
      }
      return state;
    },
  });

  useEffect(() => {
    // Ensure the display status is set to hidden when the location is changed to unassigned (which has the value of the parent location id).
    if (editingRowData && isUnassignedLocation) {
      const disableSwitch = !isParentLocationId;
      seedValues({
        hidden: disableSwitch,
      });
    }
  }, [editingRowData, seedValues, isUnassignedLocation, isParentLocationId]);

  if (colId === 'Icon') {
    if (!editingRowData?.avatarInfo) return null;
    return (
      <OperatoryAvatarSelector
        value={editingRowData.avatarInfo}
        disabled={isUnassignedLocation}
        onChange={(newAvatarInfo) => {
          if (editingRowData) {
            setEditingRowData({
              ...editingRowData,
              avatarInfo: newAvatarInfo,
            });
          }
        }}
      />
    );
  }

  if (colId === 'DisplayName') {
    return <TextField {...getFieldProps('displayName')} label='' disabled={isUnassignedLocation} />;
  }

  if (colId === 'LocationId') {
    return (
      <DropdownField
        {...getFieldProps('locationId')}
        label=''
        startAdornment={<Icon name='location-small' css={{ marginRight: theme.spacing(1) }} />}
        css={{ maxWidth: 225 }}
      >
        {locationOptions?.map(({ label, value }) => (
          <DropdownField.Option key={value} value={value}>
            {label}
          </DropdownField.Option>
        ))}
      </DropdownField>
    );
  }

  if (colId === 'Hidden') {
    const disableSwitch = parentLocationId ? parentLocationId === editingRowData?.locationId : false;
    return <SwitchField {...getFieldProps('hidden')} label='' disabled={disableSwitch} />;
  }

  return null;
};

type InlineEditActionProps = {
  isLoading?: boolean;
  onSave: () => void;
  onCancel: () => void;
};

export const InlineEditAction = ({ isLoading, onSave, onCancel }: InlineEditActionProps) => {
  const { t } = useTranslation('schedule-settings');

  return (
    <div
      css={{
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.75),
        marginRight: theme.spacing(3),
        backgroundColor: theme.colors.white,
        height: 'inherit',
      }}
    >
      <Button
        iconName='x'
        onClick={onCancel}
        variant='secondary'
        title={t('Cancel')}
        css={{ border: `1px solid ${theme.colors.neutral30}` }}
      />
      <Button iconName='check' onClick={onSave} loading={isLoading} title={t('Save')} />
    </div>
  );
};
